import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { IndianRupee, TrendingDown, Users, BarChart2 } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card className="border-0 shadow-sm h-100">
        <Card.Body className="d-flex align-items-center p-4">
            <div className={`p-3 rounded-circle me-3`}
                style={{ background: `var(--bs-${color}-bg-subtle, #eef2ff)` }}>
                <Icon className={`text-${color}`} size={24} />
            </div>
            <div>
                <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.06rem' }}>{title}</p>
                <h4 className="fw-bold mb-0">{value}</h4>
            </div>
        </Card.Body>
    </Card>
);

const PayrollAnalytics = ({ stats }) => {
    if (!stats) return null;

    const cur = (v) => new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0
    }).format(parseFloat(v || 0));

    // Backend returns: total_net, total_gross, total_deductions, total_employees
    // PayrollAnalytics was using: total_net_payout, total_overtime — wrong field names
    const totalNet        = parseFloat(stats.total_net  || stats.total_net_payout  || 0);
    const totalGross      = parseFloat(stats.total_gross || stats.total_base        || 0);
    const totalDeductions = parseFloat(stats.total_deductions || 0);
    const totalEmployees  = parseInt(stats.total_employees || 0);

    const grossPct = totalGross > 0 ? Math.round((totalNet        / totalGross) * 100) : 70;
    const dedPct   = totalGross > 0 ? Math.round((totalDeductions / totalGross) * 100) : 15;
    const remaining = Math.max(0, 100 - grossPct - dedPct);

    return (
        <div className="payroll-analytics mb-4">
            <Row className="g-3 mb-4">
                <Col xs={6} md={3}>
                    <StatCard title="Total Net Payout" value={cur(totalNet)}        icon={IndianRupee} color="primary"/>
                </Col>
                <Col xs={6} md={3}>
                    <StatCard title="Total Gross"      value={cur(totalGross)}       icon={BarChart2}   color="success"/>
                </Col>
                <Col xs={6} md={3}>
                    <StatCard title="Total Deductions" value={cur(totalDeductions)}  icon={TrendingDown} color="danger"/>
                </Col>
                <Col xs={6} md={3}>
                    <StatCard title="Employees Paid"   value={totalEmployees}         icon={Users}       color="info"/>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    <h6 className="fw-bold mb-3">Expenditure Breakdown</h6>
                    <div className="progress mb-3" style={{ height: 28, borderRadius: 8 }}>
                        <div className="progress-bar bg-primary fw-bold" style={{ width: `${grossPct}%` }}
                            title="Net Pay">Net Pay</div>
                        <div className="progress-bar bg-danger" style={{ width: `${dedPct}%` }}
                            title="Deductions">Deductions</div>
                        {remaining > 0 && (
                            <div className="progress-bar bg-light text-muted" style={{ width: `${remaining}%` }}/>
                        )}
                    </div>
                    <div className="d-flex flex-wrap gap-4 small text-muted">
                        <span><span className="fw-bold text-primary">▮</span> Net Pay: {cur(totalNet)}</span>
                        <span><span className="fw-bold text-danger">▮</span> Deductions: {cur(totalDeductions)}</span>
                        <span><span className="fw-bold text-success">▮</span> Total TDS: {cur(stats.total_tds)}</span>
                        <span><span className="fw-bold text-info">▮</span> Total PF: {cur(stats.total_pf)}</span>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PayrollAnalytics;
